import { useState } from "react";
import { useApiSWR } from "@/hooks/useApiSWR";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import type { EmailToTaskFormApi } from "@/hooks/useEmailToTaskForm";
import {
	listHubAgents,
	testHubConnection,
	type HubAgent,
} from "@/services/email-to-task.service";
import { SectionBlock } from "../SectionBlock";
import { Field, SecretInput, textareaClass } from "./fields";
import { TestRunDialog } from "./TestRunDialog";

export function HubCard(api: EmailToTaskFormApi) {
	const { form, setField, settings, secrets, setSecret, disabled, dirty } = api;
	// Agent list from the saved connection; a manual test replaces it.
	const savedKey =
		settings.hub_base_url && settings.hub_api_key_set
			? ["email-to-task:agents", settings.hub_base_url, settings.hub_api_key_hint]
			: null;
	const { data: saved, error: savedError, isLoading: loadingAgents } = useApiSWR(
		savedKey,
		listHubAgents,
		{ revalidateOnFocus: false, shouldRetryOnError: false },
	);
	const [testedAgents, setTestedAgents] = useState<HubAgent[] | null>(null);
	const [testing, setTesting] = useState(false);
	const [testStatus, setTestStatus] = useState<{ ok: boolean; message: string } | null>(null);
	const [testRunOpen, setTestRunOpen] = useState(false);
	const canTestRun = Boolean(settings.hub_base_url && settings.hub_api_key_set && settings.hub_agent_id);

	const agents = testedAgents ?? saved?.agents ?? [];
	const status = testStatus ?? (savedError ? { ok: false, message: savedError.message } : null);

	async function handleTest() {
		setTesting(true);
		setTestStatus(null);
		try {
			const typedKey = secrets.hub_api_key?.trim();
			const res = await testHubConnection({
				hub_base_url: form.hub_base_url,
				...(typedKey ? { hub_api_key: typedKey } : {}),
			});
			setTestedAgents(res.agents);
			setTestStatus({ ok: true, message: res.message });
		} catch (err) {
			setTestStatus({ ok: false, message: (err as Error).message });
			toast.error((err as Error).message);
		} finally {
			setTesting(false);
		}
	}

	const options = agents.map((a) => ({
		value: a.id,
		label: a.enabled ? a.name : `${a.name} (disabled)`,
		description: a.description ?? undefined,
		disabled: !a.enabled,
	}));
	// Keep the saved agent visible even before the list loads.
	if (form.hub_agent_id && !options.some((o) => o.value === form.hub_agent_id)) {
		options.unshift({
			value: form.hub_agent_id,
			label: form.hub_agent_name ?? form.hub_agent_id,
			description: undefined,
			disabled: false,
		});
	}

	return (
		<SectionBlock
			title="AI Agent Hub"
			description="The selected agent reads each email and proposes tasks. TaskHub validates the result; the agent never writes tasks."
		>
			<div className="grid gap-4 sm:grid-cols-2">
				<Field label="Hub API URL" htmlFor="ett-hub-url" hint="The Hub API server, not the web app.">
					<Input
						id="ett-hub-url"
						type="url"
						placeholder="https://api.your-hub.com"
						value={form.hub_base_url ?? ""}
						onChange={(e) => setField("hub_base_url", e.target.value || null)}
						disabled={disabled}
					/>
				</Field>
				<Field label="Hub API key" htmlFor="ett-hub-key">
					<SecretInput
						id="ett-hub-key"
						isSet={settings.hub_api_key_set}
						hint={settings.hub_api_key_hint}
						value={secrets.hub_api_key}
						onChange={(v) => setSecret("hub_api_key", v)}
						placeholder="utd_..."
						disabled={disabled}
					/>
				</Field>
			</div>

			<div className="mt-3 flex flex-wrap items-center gap-3">
				<Button type="button" variant="outline" size="sm" onClick={handleTest} disabled={disabled || testing}>
					{testing ? "Testing..." : "Test connection"}
				</Button>
				{status && (
					<p role="status" className={status.ok ? "text-xs text-secondary" : "text-xs text-danger"}>
						{status.message}
					</p>
				)}
			</div>

			<div className="mt-5 grid gap-4 sm:grid-cols-2">
				<Field label="AI Agent" hint="Test the connection to load agents available to this key.">
					<SearchableSelect
						value={form.hub_agent_id}
						onValueChange={(value) => {
							const agent = agents.find((a) => a.id === value);
							setField("hub_agent_id", value || null);
							setField("hub_agent_name", agent?.name ?? null);
						}}
						options={options}
						placeholder={options.length ? "Select an agent" : "No agents loaded"}
						loading={loadingAgents}
						clearable
						disabled={disabled}
						ariaLabel="AI Agent"
					/>
				</Field>
				<Field label="Request timeout (seconds)" htmlFor="ett-timeout" hint="10 to 300.">
					<Input
						id="ett-timeout"
						type="number"
						min={10}
						max={300}
						value={form.request_timeout_sec}
						onChange={(e) => setField("request_timeout_sec", Number(e.target.value))}
						disabled={disabled}
					/>
				</Field>
				<Field
					label="Extra instructions (optional)"
					htmlFor="ett-extra"
					className="sm:col-span-2"
					hint="Added to TaskHub's rules for every email, e.g. which topics belong to which project."
				>
					<textarea
						id="ett-extra"
						rows={3}
						maxLength={4000}
						className={textareaClass}
						value={form.extra_instructions ?? ""}
						onChange={(e) => setField("extra_instructions", e.target.value || null)}
						disabled={disabled}
					/>
				</Field>
			</div>

			<div className="mt-4 flex flex-wrap items-center gap-3">
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() => setTestRunOpen(true)}
					disabled={disabled || !canTestRun}
				>
					Test with sample email
				</Button>
				<p className="text-xs text-muted">
					{!canTestRun
						? "Save the Hub URL, key and agent first."
						: dirty
							? "Uses the saved settings. Save first to test your changes."
							: "Uses the saved settings."}
				</p>
			</div>
			<TestRunDialog open={testRunOpen} onOpenChange={setTestRunOpen} />
		</SectionBlock>
	);
}
